import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAuthenticatedClient, getCurrentUserId } from "@/services/client";
import { Screen } from "@/components/layout/Screen";
import { VoiceOverlay } from "@/components/layout/VoiceOverlay";
import { Card } from "@/components/ui/card";
import { IconButton } from "@/components/ui/icon-button";
import { EmptyState } from "@/components/interactions/EmptyState";
import { cn } from "@/lib/utils";

// Gamification components
import { DailyBonusPopup } from "@/components/gamification/DailyBonusPopup";
import { useCountryGreeting } from "@/components/greetings/CountryGreetings";

// Simple Mode context with fallback
let useSimpleMode: () => { isSimpleMode: boolean };
try {
  const context = require("@/contexts/SimpleModeContext");
  useSimpleMode = context.useSimpleMode;
} catch {
  useSimpleMode = () => ({ isSimpleMode: false });
}

// CBS Components & Stores
import { useCBSBatchStore } from "@/stores/cbsBatchStore";
import { useCBSScoreStore } from "@/stores/cbsScoreStore";
import { Batch } from "@/types/cbs";
import { BatchStatusCard } from "@/components/batch/BatchStatusCard";
import { EndBatchSheet, EndBatchAction } from "@/components/batch/EndBatchSheet";
import { EndBatchConfirmDialog } from "@/components/batch/EndBatchConfirmDialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

import {
  Mic,
  Camera,
  Skull,
  UtensilsCrossed,
  ShieldCheck,
  CheckCircle2,
  Bird,
  User,
  Shield,
  AlertTriangle,
  Syringe,
  ChevronRight,
  StopCircle,
  ImageIcon,
} from "lucide-react";

import { useSupervisorRole } from "@/hooks/useSupervisorData";
import { usePhotos } from "@/hooks/api/usePhotos";
import { useTodayTaskStatus } from "@/hooks/api/cbs/useTodayTaskStatus";

// Task card configuration
interface TaskCard {
  id: string;
  icon: React.ElementType;
  labelKey: string;
  route: string;
  color: string;
  bgColor: string;
}

const taskCards: TaskCard[] = [
  { 
    id: 'vaccination', 
    icon: Syringe, 
    labelKey: 'cbs.quickActions.vaccination', 
    route: '/log/vaccination', 
    color: 'text-info',
    bgColor: 'bg-info/10'
  },
  { 
    id: 'mortality', 
    icon: Skull, 
    labelKey: 'cbs.quickActions.mortality', 
    route: '/log/mortality', 
    color: 'text-destructive',
    bgColor: 'bg-destructive/10'
  },
  { 
    id: 'feed', 
    icon: UtensilsCrossed, 
    labelKey: 'cbs.quickActions.feed', 
    route: '/log/feed', 
    color: 'text-warning',
    bgColor: 'bg-warning/10'
  },
  { 
    id: 'biosecurity', 
    icon: ShieldCheck, 
    labelKey: 'cbs.quickActions.biosecurity', 
    route: '/log/biosecurity', 
    color: 'text-success',
    bgColor: 'bg-success/10'
  },
];

// Alert types
interface Alert {
  id: string;
  type: 'critical' | 'attention';
  messageKey: string;
  route: string;
  icon: React.ElementType;
}

export const HomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("home");
  const [isVoiceOpen, setIsVoiceOpen] = useState(false);
  
  // Daily bonus state
  const [showDailyBonus, setShowDailyBonus] = useState(() => {
    const lastBonus = localStorage.getItem('agxl_last_daily_bonus');
    return lastBonus !== new Date().toDateString();
  });

  // Simple mode
  const { isSimpleMode } = useSimpleMode();

  // CBS Stores
  const { activeBatch, endBatch: endBatchInStore, setActiveBatch } = useCBSBatchStore();
  const { currentScore, setCurrentScore } = useCBSScoreStore();
  const { toast } = useToast();

  const userId = getCurrentUserId();
  const queryClient = useQueryClient();
  const { data: isSupervisor } = useSupervisorRole();

  const { data: rawPhotos = [] } = usePhotos();

  // Daily login bonus — preview real points + streak for the dialog
  const { data: bonusPreview } = useQuery({
    queryKey: ['daily_bonus_preview', userId],
    queryFn: async () => {
      if (!userId) return null;
      const client = getAuthenticatedClient();

      const { data: profile } = await client
        .from('profiles')
        .select('country_id, primary_crop_id')
        .eq('id', userId)
        .maybeSingle();
      if (!profile?.country_id || !profile?.primary_crop_id) return null;

      const { data: metric } = await client
        .from('credit_metrics')
        .select('id')
        .eq('country_id', profile.country_id)
        .eq('crop_id', profile.primary_crop_id)
        .eq('is_active', true)
        .maybeSingle();
      if (!metric?.id) return null;

      const { data: rule } = await client
        .from('bonus_rules')
        .select('points_value')
        .eq('rule_code', 'STR-01')
        .eq('is_active', true)
        .eq('scoring_template_id', metric.id)
        .maybeSingle();

      const today = new Date().toISOString().split('T')[0];
      const { data: streak } = await client.rpc('compute_streak_days', {
        p_user_id: userId,
        p_reference_date: today,
      });

      return {
        points: rule?.points_value ?? 10,
        streak: Math.max(Number(streak) || 1, 1),
      };
    },
    enabled: !!userId && showDailyBonus,
    staleTime: 60_000,
  });

  // Daily login bonus — claim mutation
  const claimDailyBonus = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('not_authenticated');
      const client = getAuthenticatedClient();
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await client.rpc('claim_daily_login_bonus', {
        p_user_id: userId,
        p_reference_date: today,
      });
      if (error) throw error;
      return data as {
        awarded_points: number;
        streak_days: number;
        already_claimed: boolean;
        new_total_score?: number;
        new_bonus_score?: number;
      };
    },
    onSuccess: (data) => {
      localStorage.setItem('agxl_last_daily_bonus', new Date().toDateString());
      setShowDailyBonus(false);
      if (data.already_claimed) {
        toast({
          title: t('bonus.alreadyClaimed', 'You already claimed today!'),
        });
      } else {
        toast({
          title: t('bonus.awarded', {
            points: data.awarded_points,
            defaultValue: '+{{points}} points!',
          }),
        });
        queryClient.invalidateQueries({ queryKey: ['cbs', 'current-score', userId] });
        queryClient.invalidateQueries({ queryKey: ['daily_bonus_preview', userId] });
      }
    },
    onError: (err) => {
      console.error('Daily bonus claim failed:', err);
      toast({
        title: t('bonus.claimFailed', 'Could not claim bonus. Try again later.'),
        variant: 'destructive',
      });
    },
  });

  // Country greeting — play hello audio on home screen mount
  const storedUser = (() => {
    try {
      const raw = localStorage.getItem('agxl_user');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const { playHello } = useCountryGreeting(storedUser?.countryCode);

  useEffect(() => {
    playHello();
    // Only play once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: fetchedBatch } = useQuery({
    queryKey: ['cbs', 'active-batch', userId],
    queryFn: async () => {
      if (!userId) return null;
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('batches')
        .select('*')
        .eq('user_id', userId)
        .in('batch_status', ['active', 'placed'])
        .order('placement_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
  });

  useEffect(() => {
    if (fetchedBatch && fetchedBatch.id !== activeBatch?.id) {
      setActiveBatch(fetchedBatch as any);
    }
  }, [fetchedBatch, activeBatch?.id, setActiveBatch]);

  // Prefer fresh DB result; fall back to store only while the query is in-flight
  const currentBatch = fetchedBatch ?? activeBatch;

  const { data: fetchedScore } = useQuery({
    queryKey: ['cbs', 'current-score', userId],
    queryFn: async () => {
      if (!userId) return null;
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('credit_scores')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (fetchedScore && (!currentScore || (fetchedScore as any).updated_at !== currentScore.updated_at)) {
      setCurrentScore(fetchedScore as any);
    }
  }, [fetchedScore, currentScore, setCurrentScore]);

  // End Batch flow state
  const [showEndBatchSheet, setShowEndBatchSheet] = useState(false);
  const [endBatchAction, setEndBatchAction] = useState<EndBatchAction>("complete");
  const [endBatchReason, setEndBatchReason] = useState<string | undefined>();
  const [showEndBatchConfirm, setShowEndBatchConfirm] = useState(false);
  const [endBatchLoading, setEndBatchLoading] = useState(false);

  const handleEndBatchSelect = useCallback((action: EndBatchAction, reason?: string) => {
    setEndBatchAction(action);
    setEndBatchReason(reason);
    setShowEndBatchSheet(false);
    setShowEndBatchConfirm(true);
  }, []);

  const handleEndBatchConfirm = useCallback(async () => {
    if (!currentBatch) return;
    setEndBatchLoading(true);
    try {
      const newStatus = endBatchAction === "complete" ? "completed" : "abandoned";
      const { error } = await supabase
        .from("batches")
        .update({
          batch_status: newStatus,
          actual_harvest_date: new Date().toISOString(),
          ...(endBatchAction === "abandon" && endBatchReason
            ? { abandonment_reason: endBatchReason }
            : {}),
        })
        .eq("id", currentBatch.id);

      if (error) throw error;

      // Clear store
      endBatchInStore(currentBatch.id, new Date().toISOString());
      localStorage.removeItem("cbs-batch-storage");

      setShowEndBatchConfirm(false);
      toast({
        title: endBatchAction === "complete" ? t("cbs.batch.completedToast", "Batch Completed") : t("cbs.batch.abandonedToast", "Batch Abandoned"),
        description: t("cbs.batch.endedToast", {
          defaultValue: "Batch {{code}} has been {{status}}.",
          code: currentBatch.batch_code ?? currentBatch.id.slice(0, 8),
          status: t(`cbs.batch.status.${newStatus}`, { defaultValue: newStatus }),
        }),
      });
      navigate("/");
    } catch (err: unknown) {
      toast({
        title: t("common.error"),
        description: err instanceof Error ? err.message : t("cbs.batch.endBatchFailed", "Failed to end batch"),
        variant: "destructive",
      });
    } finally {
      setEndBatchLoading(false);
    }
  }, [currentBatch, endBatchAction, endBatchReason, endBatchInStore, navigate, toast]);

  // Daily task completion — derived from DB (daily_records + vaccination_records + today's photos)
  const { completedTaskIds, totalTasks } = useTodayTaskStatus(currentBatch?.id);
  const completedCount = completedTaskIds.length;
  const completionPercent = totalTasks === 0 ? 0 : (completedCount / totalTasks) * 100;

  // Determine flock status color
  // Calculate batch day from placement date
  const batchDay = currentBatch
    ? Math.max(1, Math.floor((Date.now() - new Date(currentBatch.placement_date).getTime()) / 86400000) + 1)
    : 0;

  const liveBirds = currentBatch
    ? (currentBatch.current_bird_count ?? currentBatch.doc_count) - (currentBatch.cumulative_mortality ?? 0)
    : 0;

  const getFlockStatusColor = () => {
    if (!currentBatch) return 'from-muted/20 to-muted/5';
    const mortalityPercent = currentBatch.doc_count > 0
      ? ((currentBatch.cumulative_mortality ?? 0) / currentBatch.doc_count) * 100
      : 0;
    if (mortalityPercent > 5) return 'from-destructive/20 to-destructive/5';
    if (mortalityPercent > 3) return 'from-warning/20 to-warning/5';
    return 'from-success/20 to-success/5';
  };

  // Alerts based on batch data
  const getAlerts = (): Alert[] => {
    if (!currentBatch) return [];
    const alerts: Alert[] = [];
    const mortalityPercent = currentBatch.doc_count > 0
      ? ((currentBatch.cumulative_mortality ?? 0) / currentBatch.doc_count) * 100
      : 0;
    
    if (mortalityPercent > 5) {
      alerts.push({
        id: 'high-mortality',
        type: 'critical',
        messageKey: 'cbs.alerts.highMortality',
        route: '/log/mortality',
        icon: AlertTriangle,
      });
    }
    
    // Check if vaccination is due (day 7, 14, 21)
    if ([7, 14, 21].includes(batchDay)) {
      alerts.push({
        id: 'vaccination-due',
        type: 'attention',
        messageKey: 'cbs.alerts.vaccinationDue',
        route: '/log/vaccination',
        icon: Syringe,
      });
    }
    
    return alerts;
  };

  const alerts = getAlerts();

  // Show empty state if no active batch
  if (!currentBatch) {
    return (
      <Screen showTopBar={true} showBottomNav={true} topBarProps={{ title: t('home.title'), showLogo: true }}>
        <EmptyState
          icon={Bird}
          title={t('cbs.batch.noBatch')}
          description={t('cbs.batch.noBatchDesc')}
          action={{
            label: t('cbs.batch.new'),
            onClick: () => navigate("/batch/new"),
          }}
        />
      </Screen>
    );
  }

  // Progress ring SVG
  const ringSize = 72;
  const strokeWidth = 6;
  const radius = (ringSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (completionPercent / 100) * circumference;

  return (
    <>
      <Screen
        showTopBar={true}
        showBottomNav={true}
        topBarProps={{
          title: t('home.title'),
          showLogo: true,
        }}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        <div className="p-4 space-y-6">
          {/* Batch Status Card */}
          <BatchStatusCard />

          {/* Compact Status Bar */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className={cn(
              "rounded-2xl px-4 py-3 bg-gradient-to-br",
              getFlockStatusColor()
            )}
          >
            <div className="flex items-center gap-4">
              {/* Small Progress Ring */}
              <div className="relative flex-shrink-0">
                <svg width={ringSize} height={ringSize} className="transform -rotate-90">
                  <circle
                    cx={ringSize / 2}
                    cy={ringSize / 2}
                    r={radius}
                    fill="none"
                    stroke="hsl(var(--muted))"
                    strokeWidth={strokeWidth}
                  />
                  <motion.circle
                    cx={ringSize / 2}
                    cy={ringSize / 2}
                    r={radius}
                    fill="none"
                    stroke="hsl(var(--primary))"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    initial={{ strokeDashoffset: circumference }}
                    animate={{ strokeDashoffset }}
                    transition={{ duration: 1, ease: "easeOut" }}
                  />
                </svg>
                <motion.div
                  className="absolute inset-0 flex flex-col items-center justify-center"
                  animate={{ scale: [1, 1.08, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <span className="text-lg">🐔</span>
                  <span className="text-[10px] font-semibold text-foreground">
                    {completedCount}/{totalTasks}
                  </span>
                </motion.div>
              </div>

              {/* Day & Live count */}
              <div className="flex-1">
                <div className="text-2xl font-bold text-foreground leading-tight">
                  {t('cbs.batch.day')} {Math.min(batchDay, currentBatch.planned_cycle_days)} <span className="text-base font-normal text-muted-foreground">/ {currentBatch.planned_cycle_days}</span>
                </div>
                <div className={cn(
                  "text-sm text-muted-foreground",
                  isSimpleMode && "simple-hide"
                )}>
                  {liveBirds.toLocaleString()} {t('common.live')}
                </div>
              </div>

              {/* Overdue badge */}
              {batchDay > currentBatch.planned_cycle_days && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if ('vibrate' in navigator) navigator.vibrate(15);
                    setShowEndBatchSheet(true);
                  }}
                  className="px-3 py-1.5 rounded-full bg-warning text-warning-foreground text-xs font-semibold animate-pulse"
                >
                  {t('cbs.batch.overdue', { defaultValue: 'Batch Overdue — End Now' })}
                </button>
              )}
            </div>
          </motion.div>

          {/* MIDDLE SECTION — Daily Task Grid (2×2) */}
          <div className="grid grid-cols-2 gap-4">
            {taskCards.map((task, index) => {
              const isCompleted = completedTaskIds.includes(task.id as typeof completedTaskIds[number]);
              const TaskIcon = task.icon;
              
              return (
                <motion.button
                  key={task.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  onClick={() => {
                    // Haptic feedback
                    if ('vibrate' in navigator) navigator.vibrate(15);
                    navigate(task.route);
                  }}
                  className={cn(
                    "relative flex flex-col items-center justify-center",
                    "min-h-[120px] rounded-2xl",
                    "transition-all duration-200",
                    "active:scale-95",
                    isCompleted 
                      ? "bg-success/10 border-2 border-success" 
                      : cn("bg-card border border-border", !isCompleted && "animate-attention-pulse"),
                    task.bgColor
                  )}
                >
                  {/* Completed checkmark overlay */}
                  {isCompleted && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle2 className="w-6 h-6 text-success" />
                    </div>
                  )}

                  {/* Task icon */}
                  <TaskIcon 
                    className={cn(
                      "transition-all",
                      task.color,
                      isSimpleMode ? "w-16 h-16" : "w-12 h-12"
                    )} 
                  />

                  {/* Label - hidden in simple mode */}
                  <span 
                    className={cn(
                      "mt-2 text-sm font-medium text-foreground text-center px-2",
                      isSimpleMode && "simple-hide"
                    )}
                  >
                    {t(task.labelKey)}
                  </span>
                </motion.button>
              );
            })}
          </div>

          {/* BOTTOM SECTION — Alert Banner (only if alerts exist) */}
          {alerts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
              className="space-y-2"
            >
              {alerts.map((alert) => {
                const AlertIcon = alert.icon;
                return (
                  <button
                    key={alert.id}
                    onClick={() => {
                      if ('vibrate' in navigator) navigator.vibrate(15);
                      navigate(alert.route);
                    }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 rounded-xl",
                      "transition-all active:scale-98",
                      alert.type === 'critical' 
                        ? "bg-destructive text-destructive-foreground" 
                        : "bg-warning text-warning-foreground"
                    )}
                  >
                    <AlertIcon className="w-6 h-6 shrink-0" />
                    <span className={cn(
                      "flex-1 text-left font-medium",
                      isSimpleMode && "simple-hide"
                    )}>
                      {t(alert.messageKey, { defaultValue: alert.type === 'critical' ? 'High mortality alert' : 'Vaccination due today' })}
                    </span>
                    <ChevronRight className="w-5 h-5 shrink-0" />
                  </button>
                );
              })}
            </motion.div>
          )}

          {/* Supervisor Mode Button */}
          {isSupervisor && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Button
                variant="outline"
                className="w-full border-blue-500/50 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
                onClick={() => navigate("/supervisor")}
              >
                <Shield className="w-5 h-5 mr-2" />
                {t("supervisor.supervisorMode", "Supervisor Mode")}
              </Button>
            </motion.div>
          )}

          {/* Recent Photos — always rendered: PhotosScreen owns the zero-photo
              empty state (photos.noPhotosYet → /camera), so hiding this entry
              left farmers with no photos no route to it at all. */}
          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate("/photos")}
          >
            <ImageIcon className="w-5 h-5 mr-2" />
            {rawPhotos.length > 0
              ? t("home.recentPhotosCount", {
                  count: rawPhotos.length,
                  defaultValue: "Recent Photos ({{count}})",
                })
              : t("home.recentPhotos", "Recent Photos")}
            <ChevronRight className="w-4 h-4 ml-auto" />
          </Button>

          {/* End Batch Button */}
          <Button
            variant="outline"
            className="w-full border-destructive/50 text-destructive hover:bg-destructive/10"
            onClick={() => setShowEndBatchSheet(true)}
          >
            <StopCircle className="w-5 h-5 mr-2" />
            {t("cbs.batch.endBatch", { defaultValue: "End Batch" })}
          </Button>
        </div>
      </Screen>

      {/* End Batch Sheet */}
      {currentBatch && (
        <EndBatchSheet
          open={showEndBatchSheet}
          onOpenChange={setShowEndBatchSheet}
          batch={currentBatch as Batch}
          onConfirm={handleEndBatchSelect}
        />
      )}

      {/* End Batch Confirm Dialog */}
      {currentBatch && (
        <EndBatchConfirmDialog
          open={showEndBatchConfirm}
          onOpenChange={setShowEndBatchConfirm}
          action={endBatchAction}
          batchCode={currentBatch.batch_code ?? currentBatch.id.slice(0, 8)}
          abandonReason={endBatchReason}
          loading={endBatchLoading}
          onConfirm={handleEndBatchConfirm}
        />
      )}

      {/* Daily Bonus Popup */}
      <DailyBonusPopup
        isOpen={showDailyBonus}
        bonusPoints={bonusPreview?.points ?? 10}
        streakDays={bonusPreview?.streak ?? 1}
        onClaim={() => {
          if (claimDailyBonus.isPending) return;
          claimDailyBonus.mutate();
        }}
      />

      {/* Voice Overlay */}
      <VoiceOverlay
        isOpen={isVoiceOpen}
        onClose={() => setIsVoiceOpen(false)}
      />
    </>
  );
};
